'use client';
import React, { useState, forwardRef, useRef, useImperativeHandle } from 'react';
import StickyHeaderAreaInner from './StickyHeaderAreaInner.mjs';
import useApplyLogging from '../../hooks/useApplyLogging.mjs';

/**
 * 스크롤 인터랙션과 고정영역을 제어하는 유틸성 컴포넌트
 *
 *
 * ### Usage
 * #### (1) 기본
 * ```jsx
 * <StickyHeaderArea
 *     header={<HeaderBrandMain {...headerProps} />}>
 *     {children}
 * </StickyHeaderArea>
 * ```
 *
 * #### (2) 동적 헤더
 *
 * ```jsx
 * const ref = useRef<HTMLElement>(null);
 *
 * return (
 *     <StickyHeaderArea
 *         header={<HeaderBrandMain {...headerProps} />}
 *         interfaceRef={ref}>
 *         {children}
 *     </StickyHeaderArea>
 * )
 * ```
 *
 * #### (3) 스크롤 대상 엘리먼트 지정
 * ```jsx
 * const $scrollTarget = useRef<HTMLDivElement>(null);
 * const [scrollTargetState, setScrollTargetState] = useState<HTMLDivElement>();
 *
 * useEffect(() => {
 *     $scrollTarget.current && setScrollTargetState($scrollTarget.current);
 * }, [$scrollTarget]);
 *
 * return (
 *     <div
 *         className="scroll-target"
 *         ref={$scrollTarget}>
 *         <StickyHeaderArea
 *             header={<HeaderBrandMain {...headerProps} />}
 *             scrollTarget={scrollTargetState}>
 *             {children}
 *         </StickyHeaderArea>
 *     </div>
 * )
 * ```
 *
 * #### (4) 단일 헤더
 * ```jsx
 * <StickyHeaderArea
 *     header={<HeaderBrandMain {...headerProps} />}
 * />
 * ```
 */
function StickyHeaderArea(_ref) {
  let {
    header,
    children,
    scrollTarget,
    interfaceRef,
    initialHeightForSsr,
    isFixedHeader = true
  } = _ref;
  useApplyLogging('StickyHeaderArea');
  const [height, setHeight] = useState(initialHeightForSsr ?? 0);
  if (interfaceRef) {
    return /*#__PURE__*/React.createElement(StickyHeaderAreaRef, {
      ref: interfaceRef,
      header: header,
      scrollTarget: scrollTarget,
      initialHeightForSsr: initialHeightForSsr,
      isFixedHeader: isFixedHeader
    }, children);
  }
  return /*#__PURE__*/React.createElement("div", {
    style: {
      height: `${height}px`
    }
  }, /*#__PURE__*/React.createElement(StickyHeaderAreaInner, {
    header: header,
    scrollTarget: scrollTarget,
    setHeight: setHeight,
    isFixedHeader: isFixedHeader
  }, children));
}
const StickyHeaderAreaRef = /*#__PURE__*/forwardRef(function StickyHeaderAreaRef(_ref2, stickyHeaderAreaRef) {
  let {
    header,
    children,
    scrollTarget,
    initialHeightForSsr,
    isFixedHeader = true
  } = _ref2;
  const [height, setHeight] = useState(initialHeightForSsr ?? 0);
  const $containerRef = useRef(null);
  useImperativeHandle(stickyHeaderAreaRef, () => ({
    updateHeight: () => {
      var _$containerRef$curren;
      ($containerRef === null || $containerRef === void 0 ? void 0 : $containerRef.current) && setHeight($containerRef === null || $containerRef === void 0 || (_$containerRef$curren = $containerRef.current) === null || _$containerRef$curren === void 0 ? void 0 : _$containerRef$curren.offsetHeight);
    }
  }));
  return /*#__PURE__*/React.createElement("div", {
    style: {
      height: `${height}px`
    }
  }, /*#__PURE__*/React.createElement(StickyHeaderAreaInner, {
    "$containerRef": $containerRef,
    header: header,
    scrollTarget: scrollTarget,
    setHeight: setHeight,
    isFixedHeader: isFixedHeader
  }, children));
});
StickyHeaderArea.displayName = 'StickyHeaderArea';

export { StickyHeaderAreaRef, StickyHeaderArea as default };
